/**
 * Utility functions for formatting carrier partner information
 */

/**
 * Formats the carrier partner ID to a human-readable name
 * @param {string} carrierId - Raw carrier partner ID (e.g., "PRZ_DELHIVERY_B2CSURFACE_002")
 * @returns {string} Formatted carrier name (e.g., "Delhivery Surface")
 */
export const formatCarrierName = (carrierId) => {
  if (!carrierId) return 'Unknown Carrier';
  
  // Remove all prefixes and suffixes
  let cleaned = carrierId
    .replace(/^PRZ_/, '')  // Remove PRZ_ prefix
    .replace(/^CPZ_/, '')  // Remove CPZ_ prefix
    .replace(/^WAY_/, '')  // Remove WAY_ prefix
    .replace(/^SHPWY_/, '')  // Remove SHPWY_ prefix
    .replace(/_\d{3}$/, '') // Remove _002, _001, etc.
    .replace(/_B2CSURFACE/, '_SURFACE')    // Convert _B2CSURFACE to _SURFACE (keep underscore)
    .replace(/_B2CAIR/, '_AIR')    // Convert _B2CAIR to _AIR (keep underscore)
    .replace(/_B2CEXPRESS/, '_EXPRESS')    // Convert _B2CEXPRESS to _EXPRESS (keep underscore)
    .replace(/_B2CREVERSE_SURFACE/, '_REVERSE_SURFACE')    // Handle reverse surface
    .replace(/_B2CREVERSE_AIR/, '_REVERSE_AIR')    // Handle reverse air
    .replace(/_B2C/, '')    // Remove remaining _B2C
    .replace(/_B2B/, '');    // Remove _B2B
  
  // Split by underscore to separate carrier name and service type
  const parts = cleaned.split('_').filter(p => p.length > 0);
  
  if (parts.length === 0) return 'Unknown Carrier';
  
  // Capitalize each part properly (first letter capital, rest lowercase)
  const formattedParts = parts.map(part => {
    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
  });
  
  // Join with space
  return formattedParts.join(' ');
};

/**
 * Extracts and formats error messages from verbose API error responses
 * @param {string} errorMessage - Raw error message from API
 * @param {string} errorCode - Error code from API
 * @returns {string} User-friendly error message
 */
export const formatErrorMessage = (errorMessage, errorCode) => {
  if (!errorMessage) return 'Unknown error occurred';
  
  // Pattern 1: Simple, user-friendly messages - return as is
  if (errorMessage.length < 100 && !errorMessage.includes('OMSException')) {
    return errorMessage;
  }
  
  // Pattern 2: Extract meaningful error from OMSException
  if (errorMessage.includes('OMSException')) {
    // Try to extract specific error messages
    
    // Check for weight/dimension errors
    if (errorMessage.includes('weight:') && errorMessage.includes('can not be less than or equal to zero')) {
      const weightMatch = errorMessage.match(/weight:\s*=\s*([\d.]+)/);
      if (weightMatch) {
        return `Invalid weight: ${weightMatch[1]} - Weight must be greater than zero`;
      }
      return 'Weight must be greater than zero';
    }
    
    // Check for invalid phone number
    if (errorMessage.includes('Invalid delivery contact number') || 
        errorMessage.includes('Invalid pickup contact number')) {
      const phoneMatch = errorMessage.match(/(\+?\d{10,15})/);
      if (phoneMatch) {
        return `Invalid contact number: ${phoneMatch[1]}`;
      }
      return 'Invalid contact number provided';
    }
    
    // Check for insufficient balance
    if (errorMessage.includes('Insufficient balance in wallet') || 
        errorMessage.includes('insufficient_balance')) {
      return 'Insufficient wallet balance - Please recharge your wallet';
    }
    
    // Check for pincode serviceability issues
    if (errorMessage.includes('is non serviceable pincode') || 
        errorMessage.includes('is not serviceble')) {
      const pincodeMatch = errorMessage.match(/(\d{6})\s+is\s+non\s+serviceable|(\d{6})\s+is\s+not\s+serviceble/i);
      if (pincodeMatch) {
        const pincode = pincodeMatch[1] || pincodeMatch[2];
        return `Pincode ${pincode} is not serviceable by this carrier`;
      }
      return 'Delivery pincode is not serviceable by this carrier';
    }
    
    // Check for invalid format errors
    if (errorMessage.includes('Invalid Format Of Order Data')) {
      // Extract the specific field error
      const fieldMatch = errorMessage.match(/'([^']+)':\s*\[ErrorDetail\(string='([^']+)'/);
      if (fieldMatch) {
        const fieldName = fieldMatch[1].replace(/_/g, ' ');
        const errorDetail = fieldMatch[2];
        return `Invalid order data: ${fieldName} - ${errorDetail}`;
      }
      return 'Order data format is invalid';
    }
    
    // Check for order creation errors
    if (errorMessage.includes('Error In Order Placing To Courier Partner')) {
      // Try to extract ReturnMessage from Python dict format
      const returnMessageMatch = errorMessage.match(/'ReturnMessage':\s*'([^']+)'/);
      if (returnMessageMatch) {
        return returnMessageMatch[1];
      }
      
      // Try to extract message from JSON format
      const jsonMessageMatch = errorMessage.match(/"message":\s*"([^"]+)"/);
      if (jsonMessageMatch) {
        const msg = jsonMessageMatch[1];
        // Clean up escape characters
        return msg.replace(/\\"/g, '"').replace(/\\n/g, ' ').replace(/\s+/g, ' ').trim();
      }
      
      // Try to extract AWB and return code information
      const awbMatch = errorMessage.match(/'AWBNo':\s*'([^']+)'/);
      const returnCodeMatch = errorMessage.match(/'ReturnCode':\s*(\d+)/);
      if (awbMatch || returnCodeMatch) {
        let parts = [];
        if (returnCodeMatch) parts.push(`Error Code: ${returnCodeMatch[1]}`);
        if (awbMatch) parts.push(`AWB: ${awbMatch[1]}`);
        return parts.join(' - ');
      }
      
      return 'Error while placing order with courier partner';
    }
    
    // Check for package saving errors
    if (errorMessage.includes('Crashing while saving package')) {
      const reasonMatch = errorMessage.match(/exception\s+'([^']+)'/);
      if (reasonMatch) {
        return reasonMatch[1];
      }
      return 'Error while saving package with carrier';
    }
    
    // Generic OMSException - try to extract the core message
    // Try to extract subCode and message from JSON
    const subCodeMatch = errorMessage.match(/"subCode":\s*(\d+)/);
    const jsonMessageMatch = errorMessage.match(/"message":\s*"([^"]+)"/);
    
    if (jsonMessageMatch) {
      let msg = jsonMessageMatch[1];
      // Clean up escape characters and truncate "Failed Errors" prefix
      msg = msg.replace(/\\"/g, '"').replace(/\\n/g, ' ').replace(/\s+/g, ' ').trim();
      
      // If it starts with "Failed Errors:", try to extract the actual error
      if (msg.includes('Failed Errors:')) {
        const failedErrorMatch = msg.match(/Message:\s*([^,}]+?)(?:,Time:|$)/);
        if (failedErrorMatch) {
          return failedErrorMatch[1].trim();
        }
      }
      
      if (subCodeMatch) {
        return `Error ${subCodeMatch[1]}: ${msg}`;
      }
      return msg;
    }
    
    const messageMatch = errorMessage.match(/Message:\s*([^,}]+)/);
    if (messageMatch) {
      return messageMatch[1].trim();
    }
  }
  
  // Pattern 3: Map common error codes to user-friendly messages
  const errorCodeMessages = {
    'WAE-DPPNS': 'Delivery pincode not serviceable for prepaid orders',
    'WAE-DPE': 'Carrier partner system error',
    'WAE-DCPNS': 'Delivery pincode not serviceable for COD orders',
    'WAE-PCPNS': 'Pickup pincode not serviceable',
    'WAE-WE': 'Weight exceeds carrier limits',
    'WAE-DE': 'Dimension exceeds carrier limits',
  };
  
  if (errorCode && errorCodeMessages[errorCode]) {
    return errorCodeMessages[errorCode];
  }
  
  // If all else fails, try to get the first sentence or first 150 characters
  const firstSentence = errorMessage.split('.')[0];
  if (firstSentence.length > 0 && firstSentence.length < 200) {
    return firstSentence + '.';
  }
  
  // Last resort - truncate the message
  return errorMessage.substring(0, 150) + '...';
};

/**
 * Gets a simplified error category for grouping
 * @param {string} errorCode - Error code from API
 * @param {string} errorMessage - Error message from API
 * @returns {string} Error category
 */
export const getErrorCategory = (errorCode, errorMessage) => {
  if (!errorCode && !errorMessage) return 'Unknown';
  
  if (errorCode === 'WAE-DPPNS' || errorCode === 'WAE-DCPNS' || errorCode === 'WAE-PCPNS') {
    return 'Serviceability';
  }
  
  if (errorCode === 'WAE-WE' || errorCode === 'WAE-DE') {
    return 'Weight/Dimension';
  }
  
  if (errorMessage && errorMessage.includes('Invalid Format')) {
    return 'Data Validation';
  }
  
  if (errorCode === 'WAE-DPE') {
    return 'Carrier System';
  }
  
  return 'Technical';
};

/**
 * Validates order data and returns missing/invalid fields
 * @param {object} order - Order object
 * @returns {object} Validation result with warnings array
 */
export const validateOrderFields = (order) => {
  const warnings = [];
  
  // Check drop details
  if (!order.drop_details?.drop_poc || order.drop_details?.drop_poc.trim() === '') {
    warnings.push({
      field: 'drop_poc',
      fieldLabel: 'Customer Name',
      section: 'drop',
      message: 'Customer name is missing',
      severity: 'critical'
    });
  }
  
  if (!order.drop_details?.drop_address || order.drop_details?.drop_address.trim() === '') {
    warnings.push({
      field: 'drop_address',
      fieldLabel: 'Delivery Address',
      section: 'drop',
      message: 'Delivery address is missing',
      severity: 'critical'
    });
  }
  
  if (!order.drop_details?.drop_postal_code || order.drop_details?.drop_postal_code.trim() === '') {
    warnings.push({
      field: 'drop_postal_code',
      fieldLabel: 'Delivery Pincode',
      section: 'drop',
      message: 'Delivery pincode is missing',
      severity: 'critical'
    });
  }
  
  // Check pickup details
  if (!order.pickup_details?.pickup_poc || order.pickup_details?.pickup_poc.trim() === '') {
    warnings.push({
      field: 'pickup_poc',
      fieldLabel: 'Pickup Contact',
      section: 'pickup',
      message: 'Pickup contact is missing',
      severity: 'warning'
    });
  }
  
  if (!order.pickup_details?.pickup_address || order.pickup_details?.pickup_address.trim() === '') {
    warnings.push({
      field: 'pickup_address',
      fieldLabel: 'Pickup Address',
      section: 'pickup',
      message: 'Pickup address is missing',
      severity: 'warning'
    });
  }
  
  // Check invoice value
  if (!order.invoice_value || parseFloat(order.invoice_value) <= 0) {
    warnings.push({
      field: 'invoice_value',
      fieldLabel: 'Invoice Value',
      section: 'general',
      message: 'Invoice value is missing or invalid',
      severity: 'warning'
    });
  }
  
  // Check item names
  if (!order.item_names || order.item_names.length === 0) {
    warnings.push({
      field: 'item_names',
      fieldLabel: 'Product Items',
      section: 'general',
      message: 'No items listed in order',
      severity: 'warning'
    });
  }
  
  return {
    isValid: warnings.filter(w => w.severity === 'critical').length === 0,
    hasWarnings: warnings.length > 0,
    warnings,
    criticalCount: warnings.filter(w => w.severity === 'critical').length,
    warningCount: warnings.filter(w => w.severity === 'warning').length
  };
};

